// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useState, useEffect, useMemo } from "react";
import {
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useClient } from "../contexts/ClientContext";
import { timed, perfTotal } from "../utils/perf";
import {
  Box,
  Button,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  LinearProgress,
  Avatar,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import DownloadIcon from "@mui/icons-material/Download";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import SummaryCard from "../components/SummaryCard";
import dayjs from "dayjs";
import usePagination from "../hooks/usePagination";

/* ─── Types ─── */
interface Employee {
  id: string;
  empId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  designation: string;
  baseSalary: number;
  status: string;
  branchId: string;
  settlementAccountId: string;
  allowances: Record<string, number>;
  tdAllowancePct: number;
}
interface AdvanceRow {
  empId: string;
  amount: number;
  date: string;
}
interface AttendanceRow {
  empId: string;
  date: string;
  clockIn: { toDate: () => Date } | null;
  clockOut: { toDate: () => Date } | null;
  verified: boolean;
}
interface AllowanceType {
  id: string;
  name: string;
  amount: number;
}
interface SettlementAccount {
  id: string;
  name: string;
}
interface PublicHoliday {
  date: string;
  name: string;
}
interface LocationItem {
  id: string;
  name: string;
}

/* ─── Earned Holiday Formula ─── */
function getEarnedHolidays(workingDays: number): number {
  if (workingDays >= 24) return 4;
  if (workingDays >= 18) return 3;
  if (workingDays >= 12) return 2;
  if (workingDays >= 1) return 1;
  return 0;
}

interface PayrollLine {
  empId: string;
  empName: string;
  designation: string;
  location: string;
  baseSalary: number;
  workingDays: number;
  publicHolidayDays: number;
  earnedHolidays: number;
  totalPaidDays: number;
  daysInMonth: number;
  dailyRate: number;
  basePay: number;
  travelDiningAllowance: number;
  totalAllowances: number;
  allowanceBreakdown: Record<string, number>;
  grossSalary: number;
  totalAdvances: number;
  netSalary: number;
  settlementAccount: string;
}

export default function ManagerPage() {
  const { col } = useClient();
  const [month, setMonth] = useState(() => dayjs().format("YYYY-MM"));
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRow[]>([]);
  const [allowanceTypes, setAllowanceTypes] = useState<AllowanceType[]>([]);
  const [settlementAccounts, setSettlementAccounts] = useState<SettlementAccount[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [advances, setAdvances] = useState<AdvanceRow[]>([]);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("success");
  const [loading, setLoading] = useState(false);
  const [locationFilter, setLocationFilter] = useState("");
  // Salary Paid tracking (empId -> amount)
  const [salaryPaid, setSalaryPaid] = useState<Record<string, number>>({});
  const [editingSalaryPaid, setEditingSalaryPaid] = useState<string | null>(null);
  const [editSalaryValue, setEditSalaryValue] = useState(0);

  useEffect(() => {
    async function loadBase() {
      const t0 = performance.now();
      try {
        const [empSnap, atSnap, saSnap, locSnap] = await Promise.all([
          timed("ManagerPage.load.employees", () => getDocs(col("employees"))),
          timed("ManagerPage.load.allowanceTypes", () => getDocs(col("allowanceTypes"))),
          timed("ManagerPage.load.settlementAccounts", () => getDocs(col("settlementAccounts"))),
          timed("ManagerPage.load.branches", () => getDocs(col("branches"))),
        ]);
        setEmployees(empSnap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            empId: (data.empId as string) ?? d.id,
            firstName: (data.firstName as string) ?? "",
            lastName: (data.lastName as string) ?? "",
            fullName: (data.fullName as string) ?? "",
            designation: (data.designation as string) ?? (data.role as string) ?? "",
            baseSalary: Number(data.baseSalary ?? 0),
            status: (data.status as string) ?? "active",
            branchId: (data.branchId as string) ?? "",
            settlementAccountId: (data.settlementAccountId as string) ?? "",
            allowances: (data.allowances as Record<string, number>) ?? {},
            tdAllowancePct: Number(data.tdAllowancePct ?? 10),
          };
        }));
        setAllowanceTypes(atSnap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "", amount: Number(d.data().amount ?? 0) })));
        setSettlementAccounts(saSnap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" })));
        setLocations(locSnap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" })).sort((a, b) => {
          if (a.name.toLowerCase().startsWith("anakapalli")) return -1;
          if (b.name.toLowerCase().startsWith("anakapalli")) return 1;
          return a.name.localeCompare(b.name);
        }));
        perfTotal("ManagerPage.loadBase TOTAL", t0);
      } catch {
        setMsg("Failed to load data.");
        setMsgType("error");
      }
    }
    loadBase();
  }, []);

  // Load attendance & public holidays for selected month
  useEffect(() => {
    async function loadMonth() {
      setLoading(true);
      const t0 = performance.now();
      try {
        const startDate = dayjs(month + "-01");
        const endDate = startDate.endOf("month");
        const startStr = startDate.format("YYYY-MM-DD");
        const endStr = endDate.format("YYYY-MM-DD");

        const attQ = query(
          col("attendance"),
          where("date", ">=", startStr),
          where("date", "<=", endStr)
        );

        // Primary data — attendance + public holidays
        const [attSnap, phSnap] = await Promise.all([
          timed(`ManagerPage.loadMonth.attendance(${month})`, () => getDocs(attQ)),
          timed("ManagerPage.loadMonth.publicHolidays", () => getDocs(col("publicHolidays"))),
        ]);
        setAttendance(attSnap.docs.map((d) => d.data() as AttendanceRow));
        setPublicHolidays(
          phSnap.docs
            .map((d) => d.data() as PublicHoliday)
            .filter((h) => h.date >= startStr && h.date <= endStr)
        );
        perfTotal(`ManagerPage.loadMonth TOTAL(${month})`, t0);
      } catch {
        setMsg("Failed to load attendance data.");
        setMsgType("error");
      } finally {
        setLoading(false);
      }

      // Load advances separately — may fail silently if collection doesn't exist
      try {
        const startStr = dayjs(month + "-01").format("YYYY-MM-DD");
        const endStr = dayjs(month + "-01").endOf("month").format("YYYY-MM-DD");
        const advQ = query(
          col("advances"),
          where("date", ">=", startStr),
          where("date", "<=", endStr)
        );
        const advSnap = await timed(`ManagerPage.loadMonth.advances(${month})`, () => getDocs(advQ));
        setAdvances(advSnap.docs.map((d) => d.data() as AdvanceRow));
      } catch {
        setAdvances([]);
      }
    }
    loadMonth();
  }, [month]);

  const getLocName = (id: string) => locations.find((l) => l.id === id)?.name ?? "—";
  const getAccName = (id: string) => {
    const sa = settlementAccounts.find((a) => a.id === id);
    return sa ? sa.name : "—";
  };

  // Calculate payroll
  const payrollLines: PayrollLine[] = useMemo(() => {
    const activeEmps = employees.filter((e) => e.status === "active");
    const filtered = locationFilter
      ? activeEmps.filter((e) => e.branchId === locationFilter)
      : activeEmps;
    const daysInMonth = dayjs(month + "-01").daysInMonth();

    return filtered.map((emp) => {
      // Count unique working days from attendance
      const empAttendance = attendance.filter((a) => a.empId === emp.id || a.empId === emp.empId);
      const uniqueDates = new Set(empAttendance.map((a) => a.date));
      const workingDays = uniqueDates.size;

      // Count public holidays in month
      const publicHolidayDays = publicHolidays.length;

      // Earned holidays from formula
      const earnedHolidays = getEarnedHolidays(workingDays);

      // Total paid days = working days + earned holidays + public holidays
      const totalDays = workingDays + earnedHolidays + publicHolidayDays;
      const totalPaidDays = totalDays;

      // Daily rate based on calendar days in month
      const dailyRate = emp.baseSalary / daysInMonth;

      // Base pay for worked + earned + public holiday days
      const basePay = dailyRate * totalPaidDays;

      // Travel & Dining Allowance
      const travelDiningAllowance = basePay * (emp.tdAllowancePct / 100);

      // Allowances
      const allowanceBreakdown: Record<string, number> = {};
      let totalAllowances = 0;
      for (const at of allowanceTypes) {
        const amt = emp.allowances[at.id] ?? at.amount ?? 0;
        allowanceBreakdown[at.name] = amt;
        totalAllowances += amt;
      }

      // Gross
      const grossSalary = basePay + travelDiningAllowance + totalAllowances;

      // Advances
      const empAdvances = advances.filter((a) => a.empId === emp.id || a.empId === emp.empId);
      const totalAdvances = empAdvances.reduce((s, a) => s + a.amount, 0);
      const netSalary = grossSalary - totalAdvances;

      const empName = emp.firstName && emp.lastName
        ? `${emp.firstName} ${emp.lastName}`
        : emp.fullName || emp.empId;

      return {
        empId: emp.empId || emp.id,
        empName,
        designation: emp.designation,
        location: getLocName(emp.branchId),
        baseSalary: emp.baseSalary,
        workingDays,
        publicHolidayDays,
        earnedHolidays,
        totalPaidDays,
        daysInMonth,
        dailyRate,
        basePay,
        travelDiningAllowance,
        totalAllowances,
        allowanceBreakdown,
        grossSalary,
        totalAdvances,
        netSalary,
        settlementAccount: getAccName(emp.settlementAccountId),
      };
    });
    // eslint-disable-next-line
  }, [employees, attendance, allowanceTypes, settlementAccounts, publicHolidays, advances, month, locationFilter, locations]);

  // Totals
  const totals = useMemo(() => {
    return payrollLines.reduce(
      (acc, l) => ({
        grossSalary: acc.grossSalary + l.grossSalary,
        totalAdvances: acc.totalAdvances + l.totalAdvances,
        netSalary: acc.netSalary + l.netSalary,
        totalAllowances: acc.totalAllowances + l.totalAllowances,
      }),
      { grossSalary: 0, totalAdvances: 0, netSalary: 0, totalAllowances: 0 }
    );
  }, [payrollLines]);

  const payPag = usePagination(payrollLines);

  const fmt = (n: number) => n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

  // Export CSV
  const exportPayrollCsv = () => {
    const atNames = allowanceTypes.map((at) => at.name);
    const header = ["ID", "Employee", "Designation", "Location", "Base Salary", "Working Days", "Public Holidays", "Earned Holidays", "Total Paid Days", "Base Pay", "T&D Allowance", ...atNames, "Total Allowances", "Gross Salary", "Advances", "Salary Paid", "Balance", "Settlement Account"].join(",") + "\n";
    const rows = payrollLines.map((l) => {
      const atValues = atNames.map((n) => l.allowanceBreakdown[n] ?? 0);
      return [
        `"${l.empId}"`, `"${l.empName}"`, `"${l.designation}"`, `"${l.location}"`,
        l.baseSalary.toFixed(0), l.workingDays, l.publicHolidayDays, l.earnedHolidays, l.totalPaidDays,
        l.basePay.toFixed(0), l.travelDiningAllowance.toFixed(0), ...atValues.map((v) => v.toFixed(0)),
        l.totalAllowances.toFixed(0), l.grossSalary.toFixed(0), l.totalAdvances.toFixed(0), l.netSalary.toFixed(0), "0",
        `"${l.settlementAccount}"`,
      ].join(",");
    }).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payroll-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setMsg("Payroll CSV downloaded.");
    setMsgType("success");
  };


  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 800 }}>
        Payroll Dashboard
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Calculate and review monthly payroll. Salary = (Daily Rate × Paid Days) + T&D Allowance + Allowances.
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <PaymentsIcon color="primary" />
          <Typography variant="h6">Payroll Month</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="month"
            label="Month"
            InputLabelProps={{ shrink: true }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Location</InputLabel>
            <Select value={locationFilter} label="Location" onChange={(e: SelectChangeEvent) => setLocationFilter(e.target.value)}>
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportPayrollCsv}>
            Export CSV
          </Button>
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <SummaryCard icon={<PeopleIcon />} value={payrollLines.length} label="Employees" color="#F9A825" />
        <SummaryCard icon={<PaymentsIcon />} value={fmt(totals.grossSalary)} label="Gross Salary" color="#1565C0" />
        <SummaryCard icon={<PaymentsIcon />} value={fmt(totals.totalAdvances)} label="Total Advances" color="#C62828" />
        <SummaryCard icon={<PaymentsIcon />} value={fmt(totals.netSalary)} label="Net Salary" color="#2E7D32" />
      </Box>

      {/* Payroll Table */}
      <Paper sx={{ overflow: "auto" }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Work Days</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Earned Hol.</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Paid Days</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Base Pay</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>T&D</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Allowances</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Gross</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Advances</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Net Salary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Salary Paid</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payrollLines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <PaymentsIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                    No active employees for this month.
                  </TableCell>
                </TableRow>
              )}
              {payPag.paginatedItems.map((l) => (
                <TableRow key={l.empId} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}>{l.empName.charAt(0)}</Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.empName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={l.workingDays} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={`${l.workingDays} days worked → +${l.earnedHolidays} earned${l.publicHolidayDays > 0 ? ` + ${l.publicHolidayDays} public holidays` : ""}`}>
                      <Chip label={`+${l.earnedHolidays}`} size="small" color="primary" variant="outlined" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{l.totalPaidDays}</TableCell>
                  <TableCell align="right">{fmt(l.basePay)}</TableCell>
                  <TableCell align="right" sx={{ color: "primary.main" }}>{fmt(l.travelDiningAllowance)}</TableCell>
                  <TableCell align="right">{fmt(l.totalAllowances)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(l.grossSalary)}</TableCell>
                  <TableCell align="right" sx={{ color: l.totalAdvances > 0 ? "error.main" : "text.secondary" }}>{l.totalAdvances > 0 ? fmt(l.totalAdvances) : "—"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(l.netSalary)}</TableCell>
                  <TableCell align="right">
                    {editingSalaryPaid === l.empId ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <TextField type="number" size="small" sx={{ width: 100 }} value={editSalaryValue || ""}
                          onChange={(e) => setEditSalaryValue(parseFloat(e.target.value) || 0)}
                          onKeyDown={(e) => { if (e.key === "Enter") { setSalaryPaid((prev) => ({ ...prev, [l.empId]: editSalaryValue })); setEditingSalaryPaid(null); } }} />
                        <Button size="small" variant="contained" sx={{ minWidth: 0, px: 1 }}
                          onClick={() => { setSalaryPaid((prev) => ({ ...prev, [l.empId]: editSalaryValue })); setEditingSalaryPaid(null); }}>OK</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: (salaryPaid[l.empId] ?? 0) > 0 ? "primary.main" : "text.secondary" }}>{fmt(salaryPaid[l.empId] ?? 0)}</Typography>
                        <IconButton size="small" onClick={() => { setEditingSalaryPaid(l.empId); setEditSalaryValue(salaryPaid[l.empId] ?? 0); }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: (l.netSalary - (salaryPaid[l.empId] ?? 0)) > 0 ? "error.main" : "primary.main" }}>
                    {fmt(l.netSalary - (salaryPaid[l.empId] ?? 0))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>{l.settlementAccount}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {payrollLines.length > 0 && (
                <TableRow sx={{ bgcolor: (t) => t.palette.mode === "light" ? "#e8f5e9" : "rgba(46,125,50,0.15)" }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 800 }}>TOTALS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(payrollLines.reduce((s, l) => s + l.basePay, 0))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(payrollLines.reduce((s, l) => s + l.travelDiningAllowance, 0))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(totals.totalAllowances)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(totals.grossSalary)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(totals.totalAdvances)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(totals.netSalary)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(payrollLines.reduce((s, l) => s + (salaryPaid[l.empId] ?? 0), 0))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(payrollLines.reduce((s, l) => s + l.netSalary - (salaryPaid[l.empId] ?? 0), 0))}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {payrollLines.length > 0 && (
          <TablePagination component="div" count={payPag.totalCount} page={payPag.page} onPageChange={payPag.onPageChange} rowsPerPage={payPag.rowsPerPage} onRowsPerPageChange={payPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
        )}
      </Paper>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={msgType} variant="filled" onClose={() => setMsg("")}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
}
